import React, { useState, useEffect, useRef, ReactNode } from 'react';
import { useReactTable, getCoreRowModel, ColumnDef, flexRender, Row } from '@tanstack/react-table';
import { BaseCard } from '@/components/uiKit/Cards/BaseCard';
import { useMousedownScroll } from '@/hooks/shared/useMousedownScroll';
import { cn } from '@/lib/utils';

// Utility to scroll to a column
const scrollToColumn = (columnId: string, columnRefs: { [key: string]: HTMLTableCellElement | null }) => {
  const columnElement = columnRefs[columnId];
  if (columnElement) {
    columnElement.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center', // Adjusts the scroll to align the column
    });
  }
};

const tableRowStyle = 'group hover:dm-bg-hover hover:cursor-pointer whitespace-nowrap max-w-screen truncate';
const tableCellStyle = `text-sm font-medium  first-of-type:text-left dm-bg-primary  group-hover:bg-inherit first-of-type:w-[250px] max-w-fit min-w-max border-[1px] whitespace-nowrap overflow-hidden text-ellipsis min-w-[200px] p-0`;

interface CollapsableTableProps<T, N> {
  mainData: any[];
  renderNestedRowCell: (row: Row<any>, column: any, index: number) => ReactNode;
  mainColumns: ColumnDef<T, any>[];
  expandedRows: Set<string>;
  setExpandedRows: React.Dispatch<React.SetStateAction<Set<string>>>;
  nestedDataKey: string;
  scrollToColumnId?: string; // Optional prop to scroll to a specific column
  classNames?: {
    baseCard?: {
      card?: string;
      content?: string;
    };
    table?: {
      container?: string;
    };
  };
}

export const CollapsableTable = <T, N>({
  mainData,
  renderNestedRowCell,
  mainColumns,
  setExpandedRows,
  expandedRows,
  nestedDataKey,
  scrollToColumnId,
  classNames,
}: CollapsableTableProps<T, N>) => {
  const [columnWidths, setColumnWidths] = useState<{ [key: string]: number }>({});
  const headerRefs = useRef<{ [key: string]: HTMLTableCellElement | null }>({});
  const cellRefs = useRef<{ [key: string]: HTMLTableCellElement | null }>({});
  const { scrollContainerRef, handleMouseDown } = useMousedownScroll();

  const mainTable = useReactTable({
    data: mainData,
    columns: mainColumns,
    getCoreRowModel: getCoreRowModel(),
  });

  // Calculate column widths after initial render (to match tables columns width)
  useEffect(() => {
    const calculateWidths = () => {
      const newWidths: { [key: string]: number } = {};

      mainTable.getHeaderGroups()[0].headers.forEach((header) => {
        if (header.column.id !== 'expander') {
          const headerWidth = headerRefs.current[header.id]?.offsetWidth || 0;
          let maxWidth = headerWidth;

          mainTable.getRowModel().rows.forEach((row) => {
            const cell = row.getVisibleCells().find((cell) => cell.column.id === header.column.id);
            if (cell) {
              const cellWidth = cellRefs.current[cell.id]?.offsetWidth || 0;
              maxWidth = Math.max(maxWidth, cellWidth);
            }
          });

          newWidths[header.id] = maxWidth;
        }
      });

      setColumnWidths(newWidths);
    };

    calculateWidths();
  }, [mainData, mainColumns]);

  useEffect(() => {
    if (scrollToColumnId) {
      scrollToColumn(scrollToColumnId, headerRefs.current); // Scroll to column if the prop is set
    }
  }, [scrollToColumnId]); // Trigger scrolling whenever this prop changes

  return (
    <BaseCard variant="wrapper" classNames={classNames?.baseCard}>
      <div ref={scrollContainerRef} className={classNames?.table?.container} onMouseDown={handleMouseDown}>
        <table className="table-auto w-full border-collapse border-spacing-y-0.5 select-none ml-[-1px]">
          {/* Header */}
          <thead className="min-w-fit sticky top-0 z-[50] dm-bg-secondary">
            <tr>
              {mainTable.getHeaderGroups()[0].headers.map((header, index) =>
                header.column.id !== 'expander' && !header.isPlaceholder ? (
                  <th
                    key={header.id}
                    ref={(el) => (headerRefs.current[header.id] = el)} // Store refs for scrolling
                    className={`py-4 text-left text-sm font-medium ${
                      index === 0 ? 'sticky left-0 z-10 dm-bg-secondary ' : ''
                    }`}
                    style={{ width: columnWidths[header.id] ? `${columnWidths[header.id]}px` : 'auto' }}
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ) : null
              )}
            </tr>
          </thead>
          <tbody className="ml-[-1px]">
            {mainTable.getRowModel().rows.map((row, rowIndex) => (
              <React.Fragment key={row.id}>
                {/* Trigger Row */}
                <tr>
                  <td className="py-2 text-sm dm-text-primary h-6 sticky left-0 z-10 border-none font-semibold dm-bg-secondary">
                    {flexRender(row.getVisibleCells()[1].column.columnDef.cell, row.getVisibleCells()[1].getContext())}
                  </td>
                  <td
                    colSpan={mainTable.getHeaderGroups()[0].headers.length - 2}
                    className="text-sm dm-text-primary h-6 dm-bg-secondary"
                  >
                    <div
                      className="flex items-center justify-between hover:cursor-pointer dm-bg-primary rounded-r-xl h-12 px-4 border-none "
                      onClick={() =>
                        setExpandedRows((prev) => {
                          const newSet = new Set(prev);
                          newSet.has(row.original.id) ? newSet.delete(row.original.id) : newSet.add(row.original.id);
                          return newSet;
                        })
                      }
                    >
                      <div className="flex items-center justify-start w-full h-full gap-2 px-2 "></div>
                    </div>
                  </td>
                </tr>
                {/* Expanded Rows */}
                {expandedRows.has(row.original.id) &&
                  row.original?.[nestedDataKey].map((nestedRow: any, index: number) => (
                    <tr key={nestedRow.id}>
                      {mainColumns
                        .filter((col) => col.id !== 'expander')
                        .map((column: any, colunIndex: number) => {
                          return (
                            <td
                              key={`${nestedRow.id}-${column.accessorKey}`}
                              ref={(el) => (cellRefs.current[column.id] = el)}
                              className={`${tableCellStyle}  capitalize ${
                                colunIndex === 0 ? 'sticky left-0 z-10 border-0 bg-gray-400' : ''
                              }`}
                              style={{ width: columnWidths[column.id] ? `${columnWidths[column.id]}px` : 'auto' }}
                            >
                              {renderNestedRowCell(nestedRow, column, colunIndex)}
                            </td>
                          );
                        })}
                    </tr>
                  ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </BaseCard>
  );
};
